import { Node as ReactFlowNode } from '@xyflow/react';
import { NodeStatusEvent } from '@/types/workflow-types';
import { FileUploadResponse } from '@/services/workflow/upload-file';

export type JsonInputValue =
  | string
  | number
  | boolean
  | null
  | unknown[]
  | Record<string, unknown>;

export type InputValue = JsonInputValue | FileUploadResponse;

export type JsonValue = JsonInputValue;

export type ValidTypes =
  | 'str'
  | 'url'
  | 'bool'
  | 'int'
  | 'float'
  | 'dict'
  | 'list'
  | 'tools'
  | 'memory';

export enum NodeType {
  GenericNode = 'GenericNode',
  StickyNoteNode = 'StickyNoteNode',
  SelectNode = 'SelectNode',
  ArtifactNode = 'ArtifactNode',
  GroupNode = 'GroupNode',
}

export type StickyNoteColor =
  | 'yellow'
  | 'pink'
  | 'blue'
  | 'green'
  | 'purple'
  | 'gray';

export const STICKY_NOTE_COLOR_OPTIONS: Record<
  StickyNoteColor,
  { bgColor: string; borderColor: string }
> = {
  yellow: { bgColor: '#FEF9C3', borderColor: '#EAB308' },
  pink: { bgColor: '#FCE7F3', borderColor: '#EC4899' },
  blue: { bgColor: '#DBEAFE', borderColor: '#3B82F6' },
  green: { bgColor: '#DCFCE7', borderColor: '#22C55E' },
  purple: { bgColor: '#F3E8FF', borderColor: '#A855F7' },
  gray: { bgColor: '#F3F4F6', borderColor: '#9CA3AF' },
};

export const DEFAULT_STICKY_NOTE_COLOR: StickyNoteColor = 'yellow';

export enum NodeCategory {
  INPUT = 'input',
  OUTPUT = 'output',
  AGENT = 'agent',
  TOOL = 'tool',
  LOGIC = 'logic',
  MEMORY = 'memory',
  GOVERNANCE = 'governance',
  DATA = 'data',
  ANNOTATION = 'annotation',
  INTEGRATION = 'integration',
}

export enum NodeInputType {
  TEXT_BOX = 'TextBox',
  TEXT_AREA = 'MultiLineText',
  SECRET_TEXT_INPUT = 'SecretTextInput',
  INTEGER = 'IntInput',
  FLOAT = 'FloatInput',
  BOOLEAN = 'BoolInput',
  OBJECT = 'DictInput',
  BINARY_TOGGLE = 'BinaryToggle',
  MULTI_SELECT = 'MultiSelect',
  DROPDOWN = 'DropDown',
  MCP_SERVERS = 'McpInput',
  LIST = 'listInput', // huh? lowercase list?
  FILE_UPLOAD = 'FileUploadDialogueBox',
  FILE_DOWNLOAD = 'FileDownload',
  AUDIO = 'AudioDisplay',
  IMAGE = 'ImageDisplay',
  VIDEO = 'VideoDisplay',
  JSON = 'JsonDisplay',
  MARKDOWN = 'MarkdownDisplay',
  TEXT_OUTPUT = 'TextDisplay',
  MCP_SERVER_CONFIG = 'MCPServerConfigDialogueBox',
  PPT_DOC_DISPLAY = 'PowerPointDocumentDisplay',
  WORD_DOC_DISPLAY = 'WordDocumentDisplay',
  EXCEL_DOC_DISPLAY = 'ExcelDocumentDisplay',
  FILE_BROWSER_DISPLAY = 'FileBrowserDisplay',
}

/** Maps an Office document file extension to its display type, for
 * inferring display_modal from a file URL when it isn't set explicitly. */
export const OFFICE_EXTENSION_DISPLAY_TYPE: Record<string, NodeInputType> = {
  ppt: NodeInputType.PPT_DOC_DISPLAY,
  pptx: NodeInputType.PPT_DOC_DISPLAY,
  doc: NodeInputType.WORD_DOC_DISPLAY,
  docx: NodeInputType.WORD_DOC_DISPLAY,
  xls: NodeInputType.EXCEL_DOC_DISPLAY,
  xlsx: NodeInputType.EXCEL_DOC_DISPLAY,
};

export const OFFICE_DOC_DISPLAY_TYPES = new Set<NodeInputType>(
  Object.values(OFFICE_EXTENSION_DISPLAY_TYPE),
);

/** Display types whose content may be offloaded to a file when it's too
 * large to deliver inline, and so may need a fetch-from-file fallback. */
export const FILE_FETCHABLE_DISPLAY_TYPES = new Set<NodeInputType>([
  NodeInputType.MARKDOWN,
  NodeInputType.TEXT_OUTPUT,
  NodeInputType.JSON,
]);

/** Canonical file extension to fall back to for each Office display type
 * when a real filename can't be recovered from the result. */
export const OFFICE_DOC_DEFAULT_EXTENSION: Partial<
  Record<NodeInputType, string>
> = {
  [NodeInputType.PPT_DOC_DISPLAY]: 'pptx',
  [NodeInputType.WORD_DOC_DISPLAY]: 'docx',
  [NodeInputType.EXCEL_DOC_DISPLAY]: 'xlsx',
};

export enum HandleType {
  STRING = 'str',
  NUMBER = 'int',
  FLOAT = 'float',
  BOOLEAN = 'bool',
  OBJECT = 'dict',
  ARRAY = 'list',
  FILE = 'file',
  // IMAGE = 'image',
  // AUDIO = 'audio',
  // VIDEO = 'video',
  // JSON = 'json',
  // MARKDOWN = 'markdown',
  URL = 'url',
  TOOL = 'tools',
  MEMORY = 'memory',
  // Add more as needed
}

// non technical labels for data types, used in tooltip UI
export const HANDLE_TYPE_LABELS: Record<string, string> = {
  str: 'Text',
  url: 'Link',
  bool: 'Yes/No',
  int: 'Number',
  float: 'Decimal number',
  dict: 'Store',
  list: 'List',
  tools: 'Tool',
  memory: 'Memory',
  file: 'File',
};

export const formatAcceptedHandleTypes = (types: string[]) =>
  types.map((t) => HANDLE_TYPE_LABELS[t] || t);

export interface NodeParam {
  field_id: string;
  display_name: string;
  display_modal: NodeInputType;
  valid_types: ValidTypes[];
  hidden: boolean; // hide whole field
  show: boolean; // with new design, if false and hidden is true, hide the whole field; if true, display the field with the label and input
  advanced: boolean;
  required: boolean;
  info: string;
  default: InputValue;
  trace: boolean;
  dynamic: boolean;
  beta: boolean;
  value: InputValue;
  placeholder?: string;
  is_list: boolean;
  plugged?: boolean;
  options?: string[];
  /** Display metadata for option values, populated server-side by
   * org_config helpers (e.g. delegable workflows: label + version info).
   * The index signature admits helper-specific extras such as
   * latest_version_id / latest_version_number. */
  options_metadata?: Array<{
    value: string;
    label: string;
    status?: string;
    [key: string]: unknown;
  }>;
  depends_on?: string;
  min_value?: number;
  max_value?: number;
  status?: string; // Connection status or other state information
  bindable_config: {
    is_bound: boolean;
    input_port: NodeInputPort;
  };
  expected_schema?: Record<string, unknown>;
  files?: NodeFile[];
}

export interface NodeInputPort {
  id: string;
  name: string;
  display_name: string;
  category: string;
  info: string;
  cardinality: 'one' | 'many';
  port_type: {
    accepted_types: HandleType[];
  };
  binding_type: 'handle' | 'param';
  incoming_connections: {
    node_id: string;
    port_id: string;
  }[];
  optional: boolean;
}

export interface NodeOutputPort {
  id: string;
  name: string;
  display_name: string;
  category: string;
  info: string;
  cardinality: 'one' | 'many';
  port_type: {
    accepted_types: HandleType[];
  };
  optional: boolean;
}

export interface NodeStruct {
  beta: boolean;
  can_be_tool: boolean;
  category: string; // unused
  tool_category?: string; // a sub-category used exclusively for tool nodes
  description: string;
  display_name: string; // backend-provided default label for the node; FE may use this as the default user-visible name, but not as a stable identity key
  name?: string; // Backend NodeStruct requires this on save; catalog nodes often omit it on the FE struct
  user_config_name?: string; // for user to customize the node name
  user_config_description?: string;
  icon: string;
  icon_color?: string;
  params: NodeParam[];
  node_id: string; // node name in snake case
  node_sub_type: string;
  node_type: NodeCategory; // a main category used for grouping nodes
  input_ports: NodeInputPort[];
  output_ports: NodeOutputPort[];
  mcp_server: string;
  show: boolean;
  stream_result: boolean;
  trace: boolean;
  version: string;
  contract_version?: string;
  catalog_contract_digest?: string;
  map_body_supported?: boolean;
  map_required_runtime_capabilities?: string[];
  map_body_contract?: MapBodyCatalogContract;
  map_runtime_limits?: MapRuntimeLimits;
  repeat_attempt_supported?: boolean;
  repeat_judge_supported?: boolean;
  repeat_catalog_digest?: string;
  repeat_required_runtime_capabilities?: string[];
  repeat_body_contract?: RepeatBodyCatalogContract;
  repeat_runtime_limits?: RepeatRuntimeLimits;
  runtime_special_case?: string;
  execution_class?:
    | 'control'
    | 'deterministic'
    | 'judgment'
    | 'effect'
    | string;
  effect_class?: 'none' | 'read' | 'write' | 'external' | 'unknown' | string;
  idempotent?: boolean;
  class_name: string;
  module: string;
  full_name: string;
  react_flow_generated_id: string; // node id created with uuid for each instance of the node
  has_chat?: boolean; // Whether the node supports chat functionality
  picker_hidden?: boolean;
  /** Stable executable contract explicitly advertised by the backend. */
  capability_identity?: string;
  /** Runtime capabilities required by this node's execution contract. */
  required_capabilities?: string[];
  config_schema?: Record<string, unknown>;
  output_contract?: Record<string, unknown>;
  catalog_visibility_gate?: string;
}

export interface NodeFile {
  id?: string; // Database ID - present for persisted files, absent for live execution
  generated: boolean;
  mime_type: string;
  name: string;
  presigned_url: string;
  download_url?: string; // URL with Content-Disposition for download with correct filename
  proxy_url?: string; // Short URL for Office Online preview (avoids URL length limit)
  s3_bucket: string;
  s3_key: string;
  s3_region: string;
}

// Markers ([1], [2], ...) match the inline citations in generated content.
export interface Citation {
  citation_id: string;
  source_id?: string;
  title: string;
  url?: string;
  source_type: 'web' | 'deep_research' | 'collection' | 'upload' | 'tool';
  query?: string;
  excerpts?: string[];
  retrieval_score?: number;
  rank?: number;
  collection_id?: string;
  collection_name?: string;
  external_id?: string;
  marker?: string;
  claim_text?: string;
  labels?: string[];
  workflow_id?: string;
  session_id?: string;
  node_id?: string;
  accessed_at?: string;
}

export interface NodeResult {
  data?: unknown;
  error?: string | null;
  failure_reason?: string | null;
  status?: string;
  reason?: string | null;
  selected_label?: string | null;
  output_ports_data?: Record<string, unknown>;
  files?: NodeFile[];
  metadata?: {
    character_count?: number;
    line_count?: number;
    word_count?: number;
    citations?: Citation[];
    [key: string]: unknown;
  };
}

export interface MapBodySlot {
  kind: 'input' | 'param';
  target_id: string;
}

export interface MapBodyCatalogContract {
  version: '1.0';
  item_slots: MapBodySlot[];
  index_slots: MapBodySlot[];
  static_params: string[];
  supports_files: false;
  supports_handles: false;
  supports_clarification: false;
}

export interface MapBodyExecutionContract {
  kind: 'call';
  semantic_child_id: string;
  catalog_node_id: string;
  node_version: string;
  node_contract_version: string;
  capability_identity: string;
  catalog_contract_digest: string;
  item_slot: MapBodySlot;
  index_slot: MapBodySlot | null;
  static_params: Record<string, JsonValue>;
  node_template: Record<string, JsonValue>;
}

export interface MapExecutionConfig {
  version: '1.0';
  semantic_id: string;
  item_name: string;
  index_name: string;
  max_items: number;
  max_concurrency: number;
  failure_mode: 'fail_fast' | 'all_settled';
  preserve_input_order: true;
  item_timeout_seconds: number;
  item_max_attempts: number;
  idempotency_mode: 'required';
  cache_mode: 'disabled';
  body: MapBodyExecutionContract | null;
  runtime_limits?: MapRuntimeLimits | null;
}

export interface MapRuntimeLimits {
  max_items: number;
  max_concurrency: number;
  max_input_bytes: number;
  max_item_bytes: number;
  max_item_result_bytes: number;
  max_result_bytes: number;
  max_history_events: number;
}

export interface MapItemOutcome {
  index: number;
  status: 'completed' | 'failed';
  value?: JsonValue;
  error?: {
    code: string;
    category: string;
    retryable: boolean;
  };
}

export interface MapControlResult {
  results: MapItemOutcome[];
  counts: { input: number; completed: number; failed: number };
  failure_mode: 'fail_fast' | 'all_settled';
  digest: string;
  provenance: Record<string, JsonValue>;
}

export interface MapProgress {
  input: number;
  admitted: number;
  completed: number;
  failed: number;
}

export interface RepeatBodySlot {
  kind: 'input' | 'param';
  target_id: string;
}

export interface RepeatBodyCatalogContract {
  version: '1.0';
  roles: ('attempt' | 'judge')[];
  input_slots: RepeatBodySlot[];
  feedback_slots: RepeatBodySlot[];
  subject_slots: RepeatBodySlot[];
  iteration_slots: RepeatBodySlot[];
  static_params: string[];
  supports_files: false;
  supports_handles: false;
  supports_clarification: false;
}

interface RepeatSlotPinBase {
  kind: 'call';
  semantic_child_id: string;
  catalog_node_id: string;
  node_version: string;
  node_contract_version: string;
  capability_identity: string;
  catalog_contract_digest: string;
  iteration_slot: RepeatBodySlot | null;
  static_params: Record<string, JsonValue>;
  node_template: Record<string, JsonValue>;
}

export interface RepeatAttemptPin extends RepeatSlotPinBase {
  input_slot: RepeatBodySlot | null;
}

export interface RepeatJudgePin extends RepeatSlotPinBase {
  subject_slot: RepeatBodySlot;
}

export interface RepeatFeedbackBinding {
  source_path: (string | number)[];
  target: RepeatBodySlot;
}

export interface RepeatExecutionConfig {
  version: '1.0';
  semantic_id: string;
  max_iterations: number;
  exit_condition: Record<string, JsonValue> | null;
  feedback_bindings: RepeatFeedbackBinding[];
  emit_mode: 'last_attempt';
  attempt: RepeatAttemptPin | null;
  judge: RepeatJudgePin | null;
  runtime_limits?: RepeatRuntimeLimits | null;
}

/**
 * Canvas-authored pseudo-cycle policy. The backend derives and pins `region`,
 * `feedback_bindings`, and `runtime_limits` during prepare; they are never
 * authored in the node card.
 */
export interface RepeatPseudoCycleConfig {
  version: '2.0';
  semantic_id: string;
  max_iterations: number;
  exit_condition: Record<string, JsonValue>;
  feedback_source_path: (string | number)[];
  feedback_bindings: RepeatFeedbackBinding[];
  emit_mode: 'last_attempt';
  region?: Record<string, JsonValue> | null;
  runtime_limits?: RepeatRuntimeLimits | null;
}

export type RepeatConfig = RepeatExecutionConfig | RepeatPseudoCycleConfig;

export interface RepeatRuntimeLimits {
  max_iterations: number;
  max_input_bytes: number;
  max_value_bytes: number;
  max_result_bytes: number;
  max_history_events: number;
}

export interface RepeatValueSummary {
  digest: string;
  bytes: number;
}

export interface RepeatIterationRecord {
  iteration: number;
  status: 'completed' | 'failed';
  attempt?: RepeatValueSummary;
  judge?: RepeatValueSummary;
  judge_value?: JsonValue;
  feedback?: Record<string, JsonValue>;
  satisfied?: boolean;
  failed_slot?: 'attempt' | 'judge';
  error?: { code: string; category: string; retryable: boolean };
}

export interface RepeatControlResult {
  final_value?: JsonValue;
  satisfied: boolean;
  exit_reason: 'satisfied' | 'max_iterations_exhausted';
  iterations: RepeatIterationRecord[];
  iteration_count: number;
  digest: string;
  provenance: Record<string, JsonValue>;
}

export interface RepeatProgress {
  iteration: number;
  max_iterations: number;
  satisfied?: boolean | null;
  exit_reason?: string | null;
}

export interface NodeData extends Record<string, unknown> {
  struct: NodeStruct;
  result?: NodeResult | NodeParam;
  nodeStatusEvents?: NodeStatusEvent[];
  isConnected?: boolean;
  isLoading?: boolean;
  isExecuting?: boolean;
  hasError?: boolean;
  isCompleted?: boolean;
  compatibleNodes?: NodeStruct[];
  acceptedTypes?: HandleType[];
  type?: 'target' | 'source';
  /**
   * True when this node's result was seeded from a previous run via
   * the rerun-from-node flow rather than freshly computed.  Drives
   * the "Cached" badge in the node card.  The editor sets it from
   * the lifecycle/rerun_seeded event's cached_node_ids list.
   */
  isCached?: boolean;
  /**
   * True when the workflow is paused AND this node was in flight
   * (begin events fired, no completion).  The activity was cancelled
   * abruptly; on resume the node will re-execute from scratch.
   * GenericNode swaps the yellow "executing" spinner for an amber
   * "paused" badge so the user can tell at a glance which nodes
   * are waiting to resume vs which are still running.
   */
  isPausedWaiting?: boolean;
  /**
   * True when the latest event for this node is ``RETRY_SCHEDULED``
   * (emitted by the BE activity wrapper between failed attempts of
   * a retryable failure) and no later begin/processing/terminal
   * event has come back yet.
   *
   * Without this flag, a transient failure (provider 429, 5xx,
   * connection blip) flips the node to red on the first attempt's
   * emit_error and stays red even when Temporal's RetryPolicy
   * successfully recovers on the next attempt.  GenericNode renders
   * an amber retrying spinner instead of the red error badge while
   * this is true.
   */
  isRetrying?: boolean;
  /** Durable control-flow terminal state, distinct from success and failure. */
  isSkipped?: boolean;
  skipReason?: string | null;
  mapProgress?: MapProgress;
  repeatProgress?: RepeatProgress;
  /**
   * True when the user can click a "Re-run from this node" play
   * button on this node card.  Computed by the editor; gates the
   * always-visible play button rendered by GenericNode.
   *
   * The predicate (see ``workflow-editor-page.tsx`` ``isRerunnable``):
   *   (isPaused || isHistoricalData)         // a context where rerun makes sense
   *   && (isCompleted || hasError)           // node has a recorded result to anchor from
   *   && !isExecuting && !isPausedWaiting    // never on a node that's mid-execute
   *
   * False / absent in normal live-run, idle, or unexecuted-node
   * states.  A click resolves to either an
   * edit-during-pause + resume (paused context) or a fresh
   * rerun-from-node start (historical context).
   */
  isRerunnable?: boolean;
}

/**
 * Data shape for sticky-note nodes (type === NodeType.StickyNoteNode).
 * Extends NodeData so the standard struct/persistence path still works,
 * adds presentation-only fields the StickyNoteNode component reads:
 *   - text             — rich-text HTML body
 *   - backgroundColor  — palette key from STICKY_NOTE_COLOR_OPTIONS
 *
 * Sticky notes are NOT executable. The backend skips them in topological
 * sort + node-access validation by node_type === "annotation".
 */
export interface StickyNoteData extends NodeData {
  text: string;
  backgroundColor: StickyNoteColor;
}

export type CustomNode = ReactFlowNode & {
  type: string;
  data?: NodeData;
};

export interface ArtifactNodeData extends Record<string, unknown> {
  file: NodeFile;
  parentNodeId: string;
  fileDetail?: string;
  // Inline body for virtual artifacts (markdown/text display outputs that
  // have no backing S3 file). When set, the card downloads from this string
  // instead of a presigned URL.
  content?: string;
  contentType?: 'markdown' | 'text';
}

// TODO: remove name field to use NodeStruct.display_name instead for consistency and remove this ApiNodeData type
// Type for the API response with node name as key
export interface ApiNodeData extends NodeStruct {
  name: string; // The node name/key from the API response
}

// Type for grouped nodes by node_type
export type GroupedNodes = Record<string, ApiNodeData[]>;

// =============================================================================
// Sticky Note constants
// =============================================================================
// The sticky-note node is NOT part of the FE catalog (BE returns show=false
// for it from /list-nodes). The FE constructs its `NodeStruct` locally so the
// existing save/load/transform path treats it like any other node — the BE
// skips it from execution by `node_type === 'annotation'`.

export const STICKY_NOTE_NODE_ID = 'sticky_note_node';
export const STICKY_NOTE_MODULE = 'nodes.annotation.sticky_note.node';
export const STICKY_NOTE_CLASS_NAME = 'StickyNoteNode';
export const STICKY_NOTE_VERSION = '1.0.0';

export const STICKY_NOTE_MIN_WIDTH = 200;
export const STICKY_NOTE_MIN_HEIGHT = 200;
export const STICKY_NOTE_DEFAULT_WIDTH = 200;
export const STICKY_NOTE_DEFAULT_HEIGHT = 200;

/** Z-index applied to sticky notes so they sit above generic nodes / edges. */
export const STICKY_NOTE_Z_INDEX = 1500;
export const STICKY_NOTE_Z_INDEX_SELECTED = 2000;

export const GROUP_NODE_ID = 'group_node';
export const GROUP_NODE_MODULE = 'nodes.annotation.group_node.node';
export const GROUP_NODE_CLASS_NAME = 'GroupNode';
export const GROUP_NODE_VERSION = '1.0.0';

/**
 * Build a minimal `NodeStruct` for a group container instance.
 *
 * Canvas-only annotation node — persisted in the workflow payload but skipped
 * from execution, same pattern as sticky notes.
 */
export function createGroupNodeStruct(
  reactFlowGeneratedId: string,
): NodeStruct {
  return {
    beta: false,
    can_be_tool: false,
    category: '',
    description: 'Visual group container for canvas nodes (non-executable).',
    display_name: 'Group',
    name: 'Group',
    icon: 'group',
    params: [],
    node_id: GROUP_NODE_ID,
    node_sub_type: 'group',
    node_type: NodeCategory.ANNOTATION,
    input_ports: [],
    output_ports: [],
    mcp_server: '',
    show: false,
    stream_result: false,
    trace: false,
    version: GROUP_NODE_VERSION,
    class_name: GROUP_NODE_CLASS_NAME,
    module: GROUP_NODE_MODULE,
    full_name: `${GROUP_NODE_MODULE}.${GROUP_NODE_CLASS_NAME}`,
    react_flow_generated_id: reactFlowGeneratedId,
  };
}

/**
 * Build a minimal `NodeStruct` for a sticky-note instance.
 *
 * Mirrors the shape the BE `AdvancedWorkflowPayload` expects so the existing
 * save → load → transform pipeline accepts sticky notes without special
 * casing. Sticky notes have empty `params` / `input_ports` / `output_ports`
 * and `show: false` so they never appear in the FE catalog.
 */
export function createStickyNoteStruct(
  reactFlowGeneratedId: string,
): NodeStruct {
  return {
    beta: false,
    can_be_tool: false,
    category: '',
    description: 'Annotate the canvas with a sticky note (non-executable).',
    display_name: 'Sticky Note',
    name: 'Sticky Note',
    icon: 'sticky-note',
    params: [],
    node_id: STICKY_NOTE_NODE_ID,
    node_sub_type: 'sticky_note',
    node_type: NodeCategory.ANNOTATION,
    input_ports: [],
    output_ports: [],
    mcp_server: '',
    show: false,
    stream_result: false,
    trace: false,
    version: STICKY_NOTE_VERSION,
    class_name: STICKY_NOTE_CLASS_NAME,
    module: STICKY_NOTE_MODULE,
    full_name: `${STICKY_NOTE_MODULE}.${STICKY_NOTE_CLASS_NAME}`,
    react_flow_generated_id: reactFlowGeneratedId,
  };
}

/**
 * Empty placeholder struct used by SelectNode until the user picks a real
 * node. Kept here (rather than in the select-node component) so the node
 * store can reference it without creating a circular dependency.
 */
export const SELECT_NODE_PLACEHOLDER_STRUCT: NodeStruct = {
  beta: false,
  can_be_tool: false,
  category: '',
  description: '',
  display_name: '',
  icon: '',
  params: [],
  node_id: '',
  node_sub_type: '',
  node_type: NodeCategory.LOGIC,
  input_ports: [],
  output_ports: [],
  mcp_server: '',
  show: true,
  stream_result: false,
  trace: false,
  version: '',
  class_name: '',
  module: '',
  full_name: '',
  react_flow_generated_id: '',
};
